import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useLive } from "../lib/use-live";
import type { AdminUser, Bet, LedgerRow, Paged } from "../types/api";
import { useDateTime } from "../lib/use-relative";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useAuth } from "../context/auth";
import {money, odds, signedMoney} from "../lib/format";
import {
  Badge,
  BetStatusBadge,
  BetTypeBadge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Panel,
  StatTile,
  Table,
  Td,
  TestBadge,
  Tr,
  TxStatusBadge,
  UserStatusBadge,
} from "../components/ui";
import {
  DeletePlayerDialog,
  EditPlayerDialog,
} from "../components/player-dialogs";
import { CreditDialog } from "../components/credit-dialogs";
import type { CreditMode } from "../components/credit-dialogs";

type Tab = "wallet" | "bets";

export function PlayerDetail() {
  const dateTime = useDateTime();
  const t = useT();
  const { id = "" } = useParams();
  const { can } = useAuth();

  const player = useLive<AdminUser>("player", { id });
  const [tab, setTab] = useState<Tab>("wallet");
  const [editProfile, setEditProfile] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Which tab the credit dialog opens on; null means it is closed.
  const [creditMode, setCreditMode] = useState<CreditMode | null>(null);

  const wallet = useLive<Paged<LedgerRow>>("ledger", { userId: id, perPage: 25 });
  const bets = useLive<Paged<Bet>>("bets", { userId: id, perPage: 25 });

  if (player.error)
    return <ErrorNote message={player.error} onRetry={player.reload} />;

  const user = player.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/players" className="text-dim text-[12px] hover:underline">
          ← All players
        </Link>
      </div>

      <Panel bodyClassName="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="display text-[24px]">{user?.username ?? "…"}</h2>
              {user ? <UserStatusBadge status={user.status} /> : null}
              {user?.isTest ? <TestBadge /> : null}
            </div>
            <p className="text-muted mt-1 text-[13px]">{user?.fullName}</p>

            {/* Everything the applicant typed, in the order the signup form
                asks for it — an operator checking an account against a support
                call is reading down the same list the player filled in. The
                account's own dates follow, which are about the account rather
                than about the person. */}
            <p className="eyebrow mt-5">{t("players.detail.registration")}</p>
            <dl className="mt-2 grid gap-x-8 gap-y-2 text-[12px] sm:grid-cols-2">
              <Detail label={t("common.username")} value={user?.username ?? "—"} />
              <Detail label={t("common.fullName")} value={user?.fullName ?? "—"} />
              <Detail label={t("common.phone")} value={user?.phone ?? "—"} />
              <Detail label={t("field.address")} value={user?.address ?? "—"} />
              <Detail label={t("field.bankName")} value={user?.bankName ?? "—"} />
              <Detail
                label={t("field.bankAccountNo")}
                value={
                  user?.bankAccountNo ? (
                    <span className="money">{user.bankAccountNo}</span>
                  ) : (
                    "—"
                  )
                }
              />
              <Detail
                label={t("common.referralCode")}
                value={
                  user?.referralCode ? (
                    <Link
                      to={`/referrals?q=${user.referralCode.code}`}
                      className="hover:underline"
                    >
                      <Badge tone="gold">{user.referralCode.code}</Badge>
                    </Link>
                  ) : (
                    "—"
                  )
                }
              />
              <Detail label={t("field.locale")} value={user?.locale ?? "—"} />
            </dl>

            <p className="eyebrow mt-5">{t("section.account")}</p>
            <dl className="mt-2 grid gap-x-8 gap-y-2 text-[12px] sm:grid-cols-2">
              <Detail label={t("col.registered")} value={dateTime(user?.createdAt)} />
              <Detail label={t("players.detail.approved")} value={dateTime(user?.approvedAt)} />
              <Detail
                label={t("col.lastSignIn")}
                value={dateTime(user?.lastLoginAt)}
              />
            </dl>
          </div>

          <div className="flex w-full max-w-xs flex-col gap-3 sm:w-auto">
            <StatTile
              label={t("players.detail.walletBalance")}
              value={user ? money(user.balanceCents) : "—"}
              tone="gold"
            />
            {/* Withdrawal is open to support: it can only reduce a balance, by a
                stated amount, with a reason. Adding credit still needs an admin,
                and the dialog opens on the tab this role can actually use. */}
            <Button
              variant="gold"
              onClick={() => setCreditMode("top_up")}
              disabled={!user}
            >
              {can("admin") ? t("players.detail.adjustCredit") : t("action.withdraw")}
            </Button>
            {can("admin") ? (
              <>
                <Button onClick={() => setEditProfile(true)} disabled={!user}>
                  Edit profile &amp; status
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setDeleting(true)}
                  disabled={!user}
                >
                  {t("audit.user.delete")}
                </Button>
              </>
            ) : (
              <p className="text-dim text-[11px]">
                {t("players.detail.needAdmin")}
              </p>
            )}
          </div>
        </div>
      </Panel>

      <div className="flex gap-2">
        <TabButton active={tab === "wallet"} onClick={() => setTab("wallet")}>
          {t("players.detail.wallet")}
        </TabButton>
        <TabButton active={tab === "bets"} onClick={() => setTab("bets")}>
          {t("players.detail.bets")}
        </TabButton>
      </div>

      {tab === "wallet" ? (
        <Panel eyebrow={t("nav.group.money")} title={t("players.detail.wallet")} bodyClassName="">
          {wallet.error ? (
            <div className="p-5">
              <ErrorNote message={wallet.error} onRetry={wallet.reload} />
            </div>
          ) : (
            <Table
              head={[
                t("col.reference"),
                t("common.type"),
                t("common.amount"),
                t("col.balanceAfter"),
                t("common.status"),
                t("common.reason"),
                t("common.when"),
              ]}
            >
              {wallet.loading ? (
                <LoadingRows columns={7} />
              ) : wallet.data && wallet.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <EmptyState
                      title={t("players.detail.noWallet")}
                      detail={t("players.detail.noWalletDetail")}
                    />
                  </Td>
                </Tr>
              ) : (
                (wallet.data?.items ?? []).map((row) => (
                  <Tr key={row.id}>
                    <Td className="money text-dim text-[11px]">
                      {row.reference}
                    </Td>
                    <Td>
                      <Badge
                        tone={row.amountCents >= 0 ? "success" : "neutral"}
                      >
                        {t(`tx.${row.type}` as TranslationKey)}
                      </Badge>
                    </Td>
                    <Td
                      className="money"
                      style={{
                        color:
                          row.amountCents >= 0
                            ? "var(--success)"
                            : "var(--info)",
                      }}
                    >
                      {signedMoney(row.amountCents)}
                    </Td>
                    <Td className="money text-muted">
                      {money(row.balanceAfterCents)}
                    </Td>
                    <Td>
                      <TxStatusBadge status={row.status} />
                    </Td>
                    <Td
                      className="text-dim max-w-[16rem] truncate text-[12px]"
                      title={row.reason ?? ""}
                    >
                      {row.reason ?? "—"}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {dateTime(row.createdAt)}
                    </Td>
                  </Tr>
                ))
              )}
            </Table>
          )}
        </Panel>
      ) : (
        <Panel eyebrow={t("nav.group.money")} title={t("players.detail.bets")} bodyClassName="">
          {bets.error ? (
            <div className="p-5">
              <ErrorNote message={bets.error} onRetry={bets.reload} />
            </div>
          ) : (
            <Table
              head={[
                t("common.round"),
                t("bets.edit.position"),
                t("col.bet"),
                t("col.stake"),
                t("games.odds"),
                t("col.result"),
                t("common.status"),
                t("col.payout"),
              ]}
            >
              {bets.loading ? (
                <LoadingRows columns={8} />
              ) : bets.data && bets.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <EmptyState title={t("players.detail.noBets")} />
                  </Td>
                </Tr>
              ) : (
                (bets.data?.items ?? []).map((bet) => (
                  <Tr key={bet.id}>
                    <Td className="money text-[12px]">#{bet.drawNo}</Td>
                    <Td className="money">{bet.position}</Td>
                    <Td>
                      <BetTypeBadge type={bet.betType} pickedNumber={bet.pickedNumber} />
                    </Td>
                    <Td className="money">{money(bet.stakeCents)}</Td>
                    <Td className="money text-dim text-[12px]">
                      {odds(bet.oddsBasisPoints)}
                    </Td>
                    <Td className="money">{bet.resultNumber ?? "—"}</Td>
                    <Td>
                      <BetStatusBadge status={bet.status} />
                    </Td>
                    <Td
                      className="money"
                      style={{
                        color:
                          bet.payoutCents > 0
                            ? "var(--success)"
                            : "var(--text-dim)",
                      }}
                    >
                      {bet.payoutCents > 0 ? money(bet.payoutCents) : "—"}
                    </Td>
                  </Tr>
                ))
              )}
            </Table>
          )}
        </Panel>
      )}

      <EditPlayerDialog
        user={editProfile ? (user ?? null) : null}
        onClose={() => setEditProfile(false)}
        onDone={player.reload}
      />
      <DeletePlayerDialog
        user={deleting ? (user ?? null) : null}
        onClose={() => setDeleting(false)}
        onDone={() => window.history.back()}
      />
      <CreditDialog
        user={creditMode ? (user ?? null) : null}
        initialMode={creditMode ?? "top_up"}
        onClose={() => setCreditMode(null)}
        onDone={() => {
          player.reload();
          wallet.reload();
        }}
      />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="eyebrow w-24 shrink-0 pt-0.5">{label}</dt>
      <dd className="text-muted min-w-0 break-words">{value}</dd>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.07em] transition-colors"
      style={
        active
          ? {
              background:
                "linear-gradient(180deg, var(--color-red-bright), var(--color-red))",
              color: "#fff",
            }
          : {
              background: "var(--surface)",
              color: "var(--text-muted)",
              border: "1px solid var(--border)",
            }
      }
    >
      {children}
    </button>
  );
}
